import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { sectionLabel, type DraftSection } from '../design-draft';

/**
 * A structural preview of the template's sections (B6.9e §15).
 *
 * **This is deliberately NOT a PDF.** The brief is explicit: if the backend has
 * no safe template-preview endpoint, do not invent a renderer in the browser.
 * There is none — B6.9b–d added no preview route, and this phase may not touch
 * the renderer — so what is drawn here is the DESIGN: which sections appear, in
 * what order, and how each divides its width. The banner says so in as many
 * words, because a preview that looked like the document would be a lie about
 * fidelity (fonts, pagination, content and page geometry are all the renderer's,
 * and none of them are modelled here).
 *
 * Everything drawn comes from the draft the MSE is editing, so the preview
 * updates live with unsaved changes — which is what makes it useful for
 * checking a reorder before saving.
 */
export function TemplateStructurePreview({ sections }: { sections: readonly DraftSection[] }) {
  const visible = sections.filter((s) => s.isVisible);
  const hidden = sections.filter((s) => !s.isVisible);

  return (
    <div className="space-y-3" data-testid="template-structure-preview">
      <p className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <FontAwesomeIcon icon={faCircleInfo} className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          <span className="font-medium text-foreground">Structure preview, not a PDF.</span> It
          shows the order and column proportions of this design, including unsaved changes. The
          generated report remains the visual truth.
        </span>
      </p>

      {/* A page-shaped frame, so the stack reads as a document without
          pretending to be one. */}
      <div className="mx-auto w-full max-w-2xl space-y-1.5 rounded-lg border border-border bg-card p-3 shadow-sm">
        {visible.length === 0 && (
          <p className="py-8 text-center text-xs text-muted-foreground" data-testid="preview-empty">
            Every section is hidden — this template would render an empty report.
          </p>
        )}

        {visible.map((section) => (
          <div
            key={section.sectionKey}
            className="rounded border border-border/70"
            data-testid={`preview-section-${section.sectionKey}`}
          >
            <div className="border-b border-border/70 bg-muted/30 px-2 py-1">
              <span className="text-[11px] font-medium text-foreground">
                {sectionLabel(section)}
              </span>
              {section.hideWhenEmpty && (
                <span className="ml-2 text-[10px] text-muted-foreground">(hidden when empty)</span>
              )}
            </div>
            {/* Column proportions, drawn to scale from the draft's fractions.
                A single-column section still draws one full-width band, which is
                exactly what the renderer does with it. */}
            <div className="flex items-stretch gap-px p-1">
              {section.columns.map((c) => (
                <div
                  key={c.position}
                  className={cn(
                    'min-w-0 rounded-sm bg-muted py-2',
                    !c.isVisible && 'opacity-40 outline-dashed outline-1 outline-border',
                  )}
                  style={{ flexBasis: `${c.widthFraction * 100}%` }}
                  data-testid={`preview-column-${section.sectionKey}-${c.position}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {hidden.length > 0 && (
        <div
          className="rounded-lg border border-dashed border-border px-3 py-2"
          data-testid="preview-hidden-sections"
        >
          <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <FontAwesomeIcon icon={faEyeSlash} className="h-3 w-3" aria-hidden="true" />
            Hidden in this design — will not render
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {hidden.map((s) => (
              <li
                key={s.sectionKey}
                className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground"
              >
                {sectionLabel(s)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
